import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { authApi, userApi, ApiError } from '@/lib/pillarApi';
import { runInBackground } from '@/lib/utils';
import { useAuthStore } from '@/store/auth';
import { createComponentLogger } from '@/lib/logging/frontend-logger';
import {
    ActiveRoleAssignmentResponse,
    SessionUserProfileDetailedResponse,
    getAllRoleAssignments,
    SessionUserProfileDetailedResponseSchema,
    SendVerificationEmailResponseSchema,
    GenerateBackupCodesResponseSchema,
    DeleteMyAccountResponseSchema,
    hasCustomerOrganizationCapacity,
} from '@pillar-portal/api-types';
import { RoleAssignmentsTable } from '@/components/profile/RoleAssignmentsTable';
import { SessionsManagementCard } from '@/components/profile/sessions-management-card';
import { MfaControlsCard } from '@/components/profile/MfaControlsCard';
import { CardColumn } from '@/components/layout/CardColumn';
import { BasicInformationCard } from '@/components/user/BasicInformationCard';
import { ConfigViewerCard } from '@/components/config/ConfigViewerCard';
import { type Status } from '@pillar-portal/database/dist/frontend';

function isCustomerPortalRoleAssignment(
    roleAssignment: ActiveRoleAssignmentResponse | null | undefined
): boolean {
    return hasCustomerOrganizationCapacity(roleAssignment ?? null);
}

function ProfileContent() {
    const log = createComponentLogger('ProfilePage');
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { logout } = useAuthStore();
    const [verificationLink, setVerificationLink] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [deleteError, setDeleteError] = useState('');
    const [backupCodes, setBackupCodes] = useState<string[]>([]);
    const [addPhoneMessage, setAddPhoneMessage] = useState<string | null>(null);

    const { user } = useAuthStore();
    const {
        data: profile,
        isLoading,
        error,
    } = useQuery<SessionUserProfileDetailedResponse | null>({
        queryKey: ['profile'],
        queryFn: async () => {
            const response = await authApi.getProfile();
            if (response === null || response === undefined) {
                return null;
            }
            return SessionUserProfileDetailedResponseSchema.parse(response);
        },
        enabled: !!user, // Only fetch if user is logged in
        staleTime: 0,
        refetchOnMount: 'always',
    });

    // Fetch active role assignment from current session
    const { data: activeRoleAssignment } = useQuery<ActiveRoleAssignmentResponse | null>({
        queryKey: ['activeRoleAssignment'],
        queryFn: async () => {
            return await authApi.getActiveRoleAssignment();
        },
        enabled: !!profile,
        staleTime: 30000,
    });

    const sendVerificationMutation = useMutation({
        mutationFn: async () => {
            const response = await authApi.requestSelfVerificationEmail();
            return SendVerificationEmailResponseSchema.parse(response);
        },
        onSuccess: (data) => {
            setVerificationLink(data.verification_link);
        },
    });

    const deleteAccountMutation = useMutation({
        mutationFn: async (password: string) => {
            const response = await authApi.deleteMyAccount(password);
            return DeleteMyAccountResponseSchema.parse(response);
        },
        onSuccess: () => {
            // Cancel all in-flight queries to prevent them from completing after deletion
            runInBackground(queryClient.cancelQueries(), (err) => {
                log.error('Error canceling queries:', err);
            });
            // Clear ALL React Query cache
            runInBackground(queryClient.clear(), (err) => {
                log.error('Error clearing query cache:', err);
            });
            // Logout user and redirect to login
            logout();
            // Use replace instead of push to prevent back button issues
            runInBackground(navigate('/login', { replace: true }), (err) => {
                log.error('Error navigating to login:', err);
            });
        },
        onError: (err: Error) => {
            if (err instanceof ApiError) {
                setDeleteError(err.message);
            } else {
                setDeleteError('Failed to delete account. Please try again.');
            }
        },
    });

    const generateBackupCodesMutation = useMutation({
        mutationFn: async () => {
            const response = await authApi.generateBackupCodes();
            return GenerateBackupCodesResponseSchema.parse(response);
        },
        onSuccess: (data) => {
            setBackupCodes(data.codes);
        },
    });

    const setEmailsMutation = useMutation({
        mutationFn: authApi.setEmails,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const setPhonesMutation = useMutation({
        mutationFn: authApi.setPhones,
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const requestAddPhoneVerificationMutation = useMutation({
        mutationFn: authApi.requestAddPhoneVerification,
        onSuccess: (response) => {
            setAddPhoneMessage(response.message);
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const verifyAddPhoneMutation = useMutation({
        mutationFn: authApi.verifyAddPhone,
        onSuccess: (response) => {
            setAddPhoneMessage(response.message);
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const updateUserMutation = useMutation({
        mutationFn: (data: {
            first_name?: string | null;
            last_name?: string | null;
            status?: Status;
        }) => {
            const userUuid = (profile ?? {
                user_uuid: '',
                first_name: null,
                last_name: null,
                primary_email: '',
                emails: [],
                totp_enabled: false,
                status: 'ACTIVE',
                default_role_assignment: null,
                other_role_assignments: [],
            }).user_uuid;
            if (userUuid.length === 0) {
                throw new Error('User not found');
            }
            return userApi.updateUser(userUuid, data);
        },
        onSuccess: () => {
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const basicInfoSaveMutation = useMutation({
        mutationFn: async (data: {
            first_name?: string | null;
            last_name?: string | null;
            primary_email?: string;
            status?: Status;
            emails?: {
                emails: string[];
                primary_email: string;
            };
            phones?: {
                phones: string[];
                primary_phone: string | null;
            };
        }) => {
            if (!profile) return;

            if (data.emails) {
                await setEmailsMutation.mutateAsync(data.emails);
            }
            if (data.phones) {
                await setPhonesMutation.mutateAsync(data.phones);
            }

            // Then handle other user updates
            const updates: {
                primary_email?: string;
                status?: Status;
                first_name?: string | null;
                last_name?: string | null;
            } = {};

            if (data.first_name !== undefined && data.first_name !== (profile.first_name ?? '')) {
                updates.first_name = data.first_name;
            }
            if (data.last_name !== undefined && data.last_name !== (profile.last_name ?? '')) {
                updates.last_name = data.last_name;
            }
            if (data.status && data.status !== profile.status) {
                updates.status = data.status;
            }

            if (Object.keys(updates).length > 0) {
                await updateUserMutation.mutateAsync(updates);
            }

            // Invalidate queries to refresh data
            runInBackground(queryClient.invalidateQueries({ queryKey: ['profile'] }), (err) => {
                log.error('Error invalidating queries:', err);
            });
        },
    });

    const handleBasicInfoSave = (data: {
        first_name?: string | null;
        last_name?: string | null;
        primary_email?: string;
        status?: Status;
        emails?: {
            emails: string[];
            primary_email: string;
        };
        phones?: {
            phones: string[];
            primary_phone: string | null;
        };
    }) => {
        basicInfoSaveMutation.mutate(data);
    };

    const handleRequestAddPhoneVerification = async (request: {
        readonly phone: string;
        readonly skip_verification?: boolean;
    }): Promise<void> => {
        setAddPhoneMessage(null);
        await requestAddPhoneVerificationMutation.mutateAsync(request);
    };

    const handleVerifyAddPhone = async (request: {
        readonly phone: string;
        readonly code: string;
    }): Promise<void> => {
        setAddPhoneMessage(null);
        await verifyAddPhoneMutation.mutateAsync(request);
    };

    // Users can always edit their own profile
    // For other admin-only features, check for SUPERADMIN or INTERNAL_ADMIN
    // For BasicInformationCard, allow editing for own profile
    const canEditProfile = true;
    const isCustomerPortalProfile: boolean =
        isCustomerPortalRoleAssignment(activeRoleAssignment);
    const activeRoleAssignmentId: number | null = profile
        ? (getAllRoleAssignments(profile).find(
              (roleAssignment) =>
                  roleAssignment.role_assignment_uuid ===
                  activeRoleAssignment?.role_assignment_uuid
          )?.role_assignment_id ?? null)
        : null;

    const handleCopyToken = () => {
        if (profile?.invitation_token) {
            runInBackground(navigator.clipboard.writeText(profile.invitation_token), (err) => {
                log.error('Error copying token to clipboard:', err);
            });
        }
    };

    const handleCopyLink = () => {
        if (verificationLink) {
            runInBackground(navigator.clipboard.writeText(verificationLink), (err) => {
                log.error('Error copying link to clipboard:', err);
            });
        }
    };

    const handleCopyBackupCodes = () => {
        runInBackground(navigator.clipboard.writeText(backupCodes.join('\n')), (err) => {
            log.error('Error copying backup codes to clipboard:', err);
        });
    };

    if (isLoading) {
        return (
            <CardColumn>
                <h1 className="text-3xl font-bold mb-8">Profile & Settings</h1>
                <p>Loading profile...</p>
            </CardColumn>
        );
    }

    if (error || !profile) {
        return (
            <CardColumn>
                <h1 className="text-3xl font-bold mb-8">Profile & Settings</h1>
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-destructive">
                            {error instanceof Error ? error.message : 'Failed to load profile'}
                        </p>
                    </CardContent>
                </Card>
            </CardColumn>
        );
    }

    return (
        <>
            <CardColumn>
                <h1 className="text-3xl font-bold mb-8">Profile & Settings</h1>
                {/* Basic Information */}
                {profile && (
                    <BasicInformationCard
                        user={profile}
                        isAdmin={canEditProfile}
                        onSave={handleBasicInfoSave}
                        onRequestAddEmailVerification={null}
                        onRequestAddPhoneVerification={handleRequestAddPhoneVerification}
                        onVerifyAddPhone={handleVerifyAddPhone}
                        isSuperadmin={false}
                        addEmailRequestMessage={null}
                        addEmailRequestError={null}
                        isRequestingAddEmail={false}
                        addPhoneRequestMessage={addPhoneMessage}
                        addPhoneRequestError={
                            requestAddPhoneVerificationMutation.error instanceof Error
                                ? requestAddPhoneVerificationMutation.error
                                : null
                        }
                        isRequestingAddPhone={requestAddPhoneVerificationMutation.isPending}
                        verifyAddPhoneError={
                            verifyAddPhoneMutation.error instanceof Error
                                ? verifyAddPhoneMutation.error
                                : null
                        }
                        isVerifyingAddPhone={verifyAddPhoneMutation.isPending}
                        isLoading={basicInfoSaveMutation.isPending}
                        error={basicInfoSaveMutation.error}
                    />
                )}

                {/* Pending Status - Show Invitation Token and Verification Link */}
                {profile.status === 'PENDING' && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Account Activation</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <Alert>
                                <AlertDescription>
                                    Your account is pending activation. Please verify your email
                                    address to complete registration.
                                </AlertDescription>
                            </Alert>

                            {profile.invitation_token && (
                                <div className="space-y-2">
                                    <Label>Invitation Token</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={profile.invitation_token}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button onClick={handleCopyToken} variant="outline">
                                            Copy
                                        </Button>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Use this token to complete your registration
                                    </p>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label>Email Verification</Label>
                                <Button
                                    onClick={() => sendVerificationMutation.mutate()}
                                    disabled={sendVerificationMutation.isPending}
                                    className="w-full"
                                >
                                    {sendVerificationMutation.isPending
                                        ? 'Generating Link...'
                                        : 'Get Verification Link'}
                                </Button>
                                <p className="text-sm text-muted-foreground">
                                    Click to generate a verification link for your email
                                </p>
                            </div>

                            {verificationLink && (
                                <div className="space-y-2">
                                    <Label>Verification Link</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={verificationLink}
                                            readOnly
                                            className="font-mono text-sm"
                                        />
                                        <Button onClick={handleCopyLink} variant="outline">
                                            Copy
                                        </Button>
                                    </div>
                                    <Alert>
                                        <AlertDescription>
                                            Open this link in your browser to verify your email
                                            address
                                        </AlertDescription>
                                    </Alert>
                                    <Button
                                        onClick={() => window.open(verificationLink, '_blank')}
                                        variant="secondary"
                                        className="w-full"
                                    >
                                        Open Verification Link
                                    </Button>
                                </div>
                            )}

                            {sendVerificationMutation.error && (
                                <Alert variant="destructive">
                                    <AlertDescription>
                                        {sendVerificationMutation.error instanceof Error
                                            ? sendVerificationMutation.error.message
                                            : 'Failed to generate verification link'}
                                    </AlertDescription>
                                </Alert>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Settings Sections */}
                {/* <Card
                    className="cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => {
                        runInBackground(navigate('/profile/emails'), (err) => {
                            log.error('Error navigating to emails page:', err);
                        });
                    }}
                >
                    <CardHeader>
                        <CardTitle>Email Addresses</CardTitle>
                        <CardDescription>
                            Manage your email addresses and set your primary email
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">
                            {profile.emails.length} email(s) configured
                        </p>
                    </CardContent>
                </Card> */}

                <Card
                    className="cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => {
                        runInBackground(navigate('/profile/password'), (err) => {
                            log.error('Error navigating to password page:', err);
                        });
                    }}
                >
                    <CardHeader>
                        <CardTitle>Password</CardTitle>
                        <CardDescription>Change your account password</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm text-muted-foreground">Last changed: N/A</p>
                    </CardContent>
                </Card>

                {isCustomerPortalProfile && (
                    <Card
                        className="cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() => {
                            runInBackground(navigate('/profile/security'), (err) => {
                                log.error('Error navigating to security page:', err);
                            });
                        }}
                    >
                        <CardHeader>
                            <CardTitle>Security</CardTitle>
                            <CardDescription>
                                Manage Multifactor Authentication, backup codes, and active sessions
                            </CardDescription>
                        </CardHeader>
                    </Card>
                )}

                {/* Multifactor Authentication */}
                {!isCustomerPortalProfile && (
                    <MfaControlsCard profile={profile} />
                )}

                {/* Backup Codes */}
                {!isCustomerPortalProfile && profile.totp_enabled && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Backup Codes</CardTitle>
                            <CardDescription>
                                Use backup codes to access your account if you lose your
                                authenticator
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {backupCodes.length === 0 ? (
                                <Button
                                    onClick={() => generateBackupCodesMutation.mutate()}
                                    disabled={generateBackupCodesMutation.isPending}
                                >
                                    {generateBackupCodesMutation.isPending
                                        ? 'Generating...'
                                        : 'Generate Backup Codes'}
                                </Button>
                            ) : (
                                <div className="space-y-4">
                                    <Alert>
                                        <AlertDescription>
                                            Save these codes in a safe place. Each code can only be
                                            used once.
                                        </AlertDescription>
                                    </Alert>
                                    <div className="p-4 bg-muted rounded-lg space-y-2">
                                        {backupCodes.map((code, idx) => (
                                            <div key={idx} className="font-mono text-sm">
                                                {code}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button onClick={handleCopyBackupCodes}>Copy Codes</Button>
                                        <Button
                                            variant="outline"
                                            onClick={() => setBackupCodes([])}
                                        >
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Roles */}
                {!isCustomerPortalProfile && profile && (
                    <Card>
                        <CardHeader>
                            <CardTitle>Organization Roles</CardTitle>
                        </CardHeader>
                        <CardContent>
                            {getAllRoleAssignments(profile).length === 0 ? (
                                <p className="text-sm text-muted-foreground">No roles assigned</p>
                            ) : (
                                <RoleAssignmentsTable user={profile} />
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Configuration Card */}
                {!isCustomerPortalProfile && profile && (
                    <ConfigViewerCard
                        roleAssignments={getAllRoleAssignments(profile)}
                        activeRoleAssignmentId={activeRoleAssignmentId}
                        canEdit={true}
                        isOwnConfig={true}
                        title="Configuration"
                        description="View and manage your configuration settings"
                    />
                )}

                {/* Active Sessions */}
                {!isCustomerPortalProfile && (
                    <SessionsManagementCard
                        mode="user"
                        showSearch={true}
                        title="Active Sessions"
                        description="Manage your active login sessions across different devices"
                        showHeader={false}
                    />
                )}

                {/* Delete Account Section */}
                <Card className="border-destructive">
                    <CardHeader>
                        <CardTitle className="text-destructive">Delete Account</CardTitle>
                        <CardDescription>Delete your account.</CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Alert variant="destructive">
                            <AlertDescription>
                                <strong>Warning:</strong> Deleting your account will permanently
                                remove all your data and you will not be able to recover it. This
                                action cannot be undone.
                            </AlertDescription>
                        </Alert>
                        <Button
                            variant="destructive"
                            className="mt-4"
                            onClick={() => setDeleteDialogOpen(true)}
                        >
                            Delete My Account
                        </Button>
                    </CardContent>
                </Card>
            </CardColumn>

            {/* Delete Account Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Account</DialogTitle>
                        <DialogDescription>
                            This action cannot be undone. This will permanently delete your account
                            and remove all your data from our servers.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label htmlFor="delete-password">Confirm Password</Label>
                            <Input
                                id="delete-password"
                                type="password"
                                value={deletePassword}
                                onChange={(e) => {
                                    setDeletePassword(e.target.value);
                                    setDeleteError('');
                                }}
                                placeholder="Enter your password to confirm"
                                disabled={deleteAccountMutation.isPending}
                            />
                            <p className="text-sm text-muted-foreground">
                                Enter your password to confirm account deletion
                            </p>
                        </div>

                        {deleteError && (
                            <Alert variant="destructive">
                                <AlertDescription>{deleteError}</AlertDescription>
                            </Alert>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setDeleteDialogOpen(false);
                                setDeletePassword('');
                                setDeleteError('');
                            }}
                            disabled={deleteAccountMutation.isPending}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={() => {
                                if (!deletePassword) {
                                    setDeleteError('Password is required');
                                    return;
                                }
                                setDeleteError('');
                                deleteAccountMutation.mutate(deletePassword);
                            }}
                            disabled={deleteAccountMutation.isPending || !deletePassword}
                        >
                            {deleteAccountMutation.isPending ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}

export default function ProfilePage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <ProfileContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
